// views/Dashboard/PainelExecutivo/exportarPainelExecutivo.ts
import { supabase } from '../../../src/lib/supabase';
import { api } from '../../../src/services/api';
import { buildSheet, downloadWorkbook, fileStamp } from '../../../src/lib/excelExport';
import { normalizeMesReferencia } from '../../../src/lib/mesReferencia';
import { derivarStatusBoleto } from '../../../src/lib/statusBoleto';
import { calcularKpis } from '../../../src/lib/financeiro/calcularKpis';
import type { FaturaPainel } from '../../../src/lib/financeiro/types';
import { calcularJanelaPeriodoDashboard } from '../../../src/lib/dashboard/periodoDashboard';
import type { PeriodoDashboard, JanelaPeriodo } from '../../../src/lib/dashboard/periodoDashboard';
import { filtrarFaturasPorPeriodo } from '../../../src/lib/dashboard/filtrarFaturasPorPeriodo';
import type { BaseDataFinanceiro } from '../../../src/lib/dashboard/filtrarFaturasPorPeriodo';
import { janelaParaDatasDia } from '../../../src/lib/dashboard/janelaParaDatasDia';
import { janelaParaPeriodoComercial } from '../../../src/lib/dashboard/janelaParaPeriodoComercial';
import { filtrarPorDepartamento } from '../../../src/lib/dashboard/filtrarNegociacoesPorDepartamento';
import type { DepartamentoComercial } from '../../../src/lib/dashboard/departamentoComercial';
import { mesesDoIntervalo, datasCorteParaRpc, calcularGeracaoMensal, calcularEsperadoMensal } from '../../../src/lib/dashboard/geracaoMensal';
import type { LinhaEnergiaAcumulada } from '../../../src/lib/dashboard/geracaoMensal';
import { computeByDay, aggregateKpis } from '../../../src/lib/marketing/agregacaoDiaria';
import { CENARIOS_PROJECAO_PADRAO, baseRecorrenteMensal, calcularProjecaoDeBaseReal } from '../../../src/lib/dashboard/projecaoFaturamento';
import { computeEventFunnel } from '../../CRM/Analytics/eventFunnel';
import { aplicarContagemFunil } from '../../../src/lib/dashboard/funilJornadaNoPeriodo';
import type { ContagemFunil } from '../../../src/lib/dashboard/funilJornadaNoPeriodo';
import type { Profile } from '../../../types';
import { buscarTodasAsFaturas } from './sections/financeiro/useFinanceiroDashboardData';
import { buscarTodasAsNegociacoes } from './sections/comercial/useComercialDashboardData';
import { buscarAgendasRecebiveis } from '../../../src/hooks/useAgendasRecebiveis';
import { agregarPorVencimento } from '../../../src/lib/recebiveisUc';

/** As áreas que têm dado para exportar. A Visão geral não tem — é navegação. */
export type AreaExportavel = 'comercial' | 'financeiro' | 'marketing' | 'geradoras';

export interface ParametrosExport {
  periodo: PeriodoDashboard;
  departamentos: DepartamentoComercial[];
  horizonteMesesProjecao: number;
  /** Qual data da fatura o período recorta no Financeiro — ver `dataDaBase`. */
  baseDataFinanceiro?: BaseDataFinanceiro;
  /** Como o funil do Comercial conta — o mesmo seletor "Contar" da tela. */
  contagemComercial?: ContagemFunil;
}

const FATOR_GERACAO_PADRAO = 4.5;

/**
 * Exporta só a área aberta na tela.
 *
 * Uma área por arquivo, e não o painel inteiro: cada área tem os filtros dela
 * (canal de venda no Comercial, base de data no Financeiro), e uma planilha
 * única com todas ignoraria esses filtros — o arquivo diria uma coisa e a tela,
 * outra. Cada aba reaproveita as mesmas funções puras da tela; nenhuma fórmula
 * nova mora aqui.
 */
export async function exportarAreaPainel(area: AreaExportavel, params: ParametrosExport): Promise<void> {
  const hoje = new Date();
  const janela = calcularJanelaPeriodoDashboard(params.periodo, hoje);
  const nome = (sufixo: string) => `painel-${sufixo}_${fileStamp()}.xlsx`;

  if (area === 'financeiro') {
    const faturasFiltradas = await carregarFaturasFiltradas(params, hoje);
    downloadWorkbook(
      [
        { name: 'Financeiro', ws: abaFinanceiro(faturasFiltradas, hoje) },
        { name: 'Projeção', ws: await abaProjecao(params) },
      ],
      nome('financeiro'),
    );
    return;
  }

  if (area === 'marketing') {
    downloadWorkbook([{ name: 'Marketing', ws: await abaMarketing(janela, hoje) }], nome('marketing'));
    return;
  }

  if (area === 'comercial') {
    downloadWorkbook([{ name: 'Comercial', ws: await abaComercial(params, janela, hoje) }], nome('comercial'));
    return;
  }

  // Geradoras: a energia INJETADA sai das faturas, então esta área também
  // precisa delas — é o mesmo número que a seção mostra na tela.
  const faturasFiltradas = await carregarFaturasFiltradas(params, hoje);
  downloadWorkbook(
    [{ name: 'Geradoras', ws: await abaGeradoras(janela, hoje, faturasFiltradas) }],
    nome('geradoras'),
  );
}

// ── Financeiro ──────────────────────────────────────────

async function carregarFaturasFiltradas(params: ParametrosExport, hoje: Date): Promise<FaturaPainel[]> {
  const brutas = await buscarTodasAsFaturas();
  const faturas: FaturaPainel[] = brutas.map((f) => ({
    ...f,
    mes_referencia: normalizeMesReferencia(f.mes_referencia),
    statusDerivado: derivarStatusBoleto(f, hoje),
  }));
  // A MESMA função da tela, com a MESMA base de data escolhida na barra: sem
  // isto, exportar "mês de vencimento" devolveria o recorte por referência.
  return filtrarFaturasPorPeriodo(faturas, params.periodo, hoje, params.baseDataFinanceiro ?? 'referencia');
}

function abaFinanceiro(faturasFiltradas: FaturaPainel[], hoje: Date) {
  const kpis = calcularKpis(faturasFiltradas, hoje);
  return buildSheet(
    ['Métrica', 'Valor'],
    [
      ['Faturamento Emitido', kpis.emitido],
      ['Recebido', kpis.recebido],
      ['Em Aberto', kpis.emAberto],
      ['Vencido', kpis.vencido],
      ['Taxa de Inadimplência (%)', kpis.taxaInadimplencia * 100],
      ['Ticket Médio', kpis.ticketMedio],
      ['Prazo Médio de Recebimento — emissão ao pagamento (dias)', kpis.prazoMedioRecebimentoDias ?? 0],
      ['Pagamento vs. vencimento (dias, negativo = adiantado)', kpis.desvioMedioVencimentoDias ?? 0],
      ['Recuperado', kpis.recuperado],
    ],
    ['txt', 'num'],
  );
}

// ── Projeção (isolada dos filtros globais, só o horizonte importa) ──────
// MESMA fonte da tela: se o Excel recalculasse por conta própria, o número
// exportado divergiria do número visto, e ninguém saberia qual vale.
async function abaProjecao(params: ParametrosExport) {
  const { agendas } = await buscarAgendasRecebiveis({ horizonteMeses: 6 });
  const totaisMes = agregarPorVencimento(agendas);
  const base = baseRecorrenteMensal(totaisMes.map((t) => t.emitido.valorBoleto + t.projetado.valorBoleto));
  const resultados = CENARIOS_PROJECAO_PADRAO.map((c) =>
    calcularProjecaoDeBaseReal(base.valorMensal, c, params.horizonteMesesProjecao),
  );
  return buildSheet(
    [
      'Cenário', 'Churn (%)', 'Inadimplência (%)', 'Emitido/mês', 'Recebido/mês',
      `Emitido no horizonte (${params.horizonteMesesProjecao}m)`, `Recebido no horizonte (${params.horizonteMesesProjecao}m)`,
    ],
    resultados.map((r) => [
      `${r.cenario.id} — ${r.cenario.nome}`,
      r.cenario.churnPct * 100,
      r.cenario.inadimplenciaPct * 100,
      r.emitidoMensal,
      r.recebidoMensal,
      r.emitidoTotalHorizonte,
      r.recebidoTotalHorizonte,
    ]),
    ['txt', 'num', 'num', 'num', 'num', 'num', 'num'],
  );
}

// ── Marketing ────────────────────────────────────────────

async function abaMarketing(janela: JanelaPeriodo | null, hoje: Date) {
  const { inicio, fim } = janelaParaDatasDia(janela, hoje);
  const metricas = await api.marketing.getMetrics(inicio, fim);
  const kpis = aggregateKpis(computeByDay(metricas));
  return buildSheet(
    ['Métrica', 'Valor'],
    [
      ['Investimento em Mídia Paga', kpis.totalSpend],
      ['Impressões', kpis.totalImpressions],
      ['Cliques no Link', kpis.totalLinkClicks],
      ['Resultados', kpis.totalResults],
      ['Custo por Resultado', kpis.avgCostPerResult],
      ['Conversas Iniciadas', kpis.totalConversations],
      ['Leads / Cadastros', kpis.totalLeads],
    ],
    ['txt', 'num'],
  );
}

// ── Comercial ────────────────────────────────────────────

async function abaComercial(params: ParametrosExport, janela: JanelaPeriodo | null, hoje: Date) {
  const [negociacoes, { data: profilesData, error: profilesErr }] = await Promise.all([
    buscarTodasAsNegociacoes(),
    supabase.from('profiles').select('*').neq('role', 'consumidor'),
  ]);
  if (profilesErr) throw profilesErr;
  const profilesById = new Map<string, Profile>();
  ((profilesData || []) as Profile[]).forEach((p) => profilesById.set(p.id, p));
  const filtradas = filtrarPorDepartamento(negociacoes, profilesById, params.departamentos);
  const { periodo, customRange } = janelaParaPeriodoComercial(janela, hoje);
  const contagem = params.contagemComercial ?? 'evento';
  const stages = aplicarContagemFunil(
    computeEventFunnel(filtradas, periodo, customRange, { incluirSaidas: true }),
    contagem, periodo, customRange,
  );
  // A coluna Contagem diz qual dos dois funis é este: os números mudam muito
  // entre eles, e o arquivo sai da tela sem o seletor ao lado.
  const rotuloContagem = contagem === 'jornada'
    ? 'Só o que aconteceu no período (todas as etapas dentro dele)'
    : 'Pela data da etapa';
  return buildSheet(
    ['Etapa', 'Quantidade', 'kWh', 'Contagem'],
    stages.map((s) => [s.label, s.leads, s.kwh, rotuloContagem]),
    ['txt', 'num', 'kwh', 'txt'],
    { widths: [20, 12, 14, 56] },
  );
}

// ── Geradoras ────────────────────────────────────────────

async function abaGeradoras(janela: JanelaPeriodo | null, hoje: Date, faturasFiltradas: FaturaPainel[]) {
  const meses = mesesDoIntervalo(janela, hoje);
  const datas = datasCorteParaRpc(meses);
  const [
    { data: acumuladoData, error: acumuladoErr },
    { data: unidadesData, error: unidadesErr },
    { data: settingData, error: settingErr },
    { data: ugsAtivasData, error: ugsErr },
    { data: saldosData, error: saldosErr },
  ] = await Promise.all([
    supabase.rpc('get_energia_acumulada_em_datas', { datas }),
    supabase.from('unidades_geradoras').select('capacidade_instalada'),
    supabase.from('system_settings').select('value').eq('key', 'geradoras_fator_geracao_kwh_por_kwp_dia').maybeSingle(),
    supabase.from('rateio_unidades_geradoras').select('id').eq('ativo', true),
    supabase.from('rateio_ancora_saldo_ug').select('rateio_unidade_geradora_id, saldo_ancora_kwh'),
  ]);
  if (acumuladoErr) throw acumuladoErr;
  if (unidadesErr) throw unidadesErr;
  if (settingErr) throw settingErr;
  if (ugsErr) throw ugsErr;
  if (saldosErr) throw saldosErr;

  const geracao = calcularGeracaoMensal(meses, (acumuladoData || []) as LinhaEnergiaAcumulada[]);
  const capacidadeTotalMwp = ((unidadesData || []) as { capacidade_instalada: number | null }[]).reduce((s, u) => s + (u.capacidade_instalada || 0), 0);
  const fatorSalvo = Number(settingData?.value);
  const fator = Number.isFinite(fatorSalvo) && fatorSalvo > 0 ? fatorSalvo : FATOR_GERACAO_PADRAO;
  const esperado = calcularEsperadoMensal(meses, capacidadeTotalMwp, fator);
  const totalGerado = geracao.reduce((s, p) => s + p.energiaGeradaKwh, 0);
  const totalEsperado = esperado.reduce((s, p) => s + p.energiaEsperadaKwh, 0);
  const injetadaTotal = faturasFiltradas.reduce((s, f) => s + (Number(f.energia_injetada_gd1_kwh) || 0) + (Number(f.energia_injetada_gd2_kwh) || 0), 0);
  const idsAtivos = new Set(((ugsAtivasData || []) as { id: string }[]).map((u) => u.id));
  const saldoTotal = ((saldosData || []) as { rateio_unidade_geradora_id: string; saldo_ancora_kwh: number | null }[])
    .filter((s) => idsAtivos.has(s.rateio_unidade_geradora_id))
    .reduce((s, r) => s + (Number(r.saldo_ancora_kwh) || 0), 0);

  return buildSheet(
    ['Métrica', 'Valor (kWh)'],
    [
      ['Energia Gerada', totalGerado],
      ['Energia Esperada', totalEsperado],
      ['Energia Injetada', injetadaTotal],
      ['Saldo Acumulado (foto atual)', saldoTotal],
    ],
    ['txt', 'kwh'],
  );
}
